/**
 * SOLOMON — Entry Point
 * Phase 1 / Day 9: Login & Authentication
 *
 * Provider order matters:
 *   AuthProvider → SolomonProvider → App
 *
 * SolomonProvider uses useAuth() to get authFetch,
 * so AuthProvider must be the outer wrapper.
 */

import { StrictMode } from 'react';
import { createRoot }  from 'react-dom/client';
import { AuthProvider }    from './context/AuthContext';
import { SolomonProvider } from './context/SolomonContext';
import App              from './App';
import OfferLanding     from './components/Offer/OfferLanding';
import PrivacyPolicy    from './components/Offer/PrivacyPolicy';

// ── Stale-chunk recovery ─────────────────────────────────────
// After a deploy, hashed lazy chunks (mermaid's per-diagram-type modules etc.)
// from the previous build no longer exist; a tab loaded pre-deploy requests
// dead URLs and the import fails. Vite dispatches 'vite:preloadError' for
// exactly this. Recover with ONE reload (picks up the new index.html and live
// chunk URLs), guarded by a sessionStorage flag so a genuinely broken
// network/deploy can't reload-loop — second failure surfaces normally.
// Guard is a timestamp with an expiry window rather than a clear-on-load flag:
// clearing on 'load' would re-arm the reload on every successful page load,
// letting a broken deploy reload once per user retry forever. Within the
// window we surface the error instead; after it expires the next deploy gets
// its own one-shot recovery.
const CHUNK_RELOAD_KEY = 'solomon:chunk-reload-at';
const CHUNK_RELOAD_WINDOW_MS = 60_000;

window.addEventListener('vite:preloadError', (event) => {
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
  if (Date.now() - last < CHUNK_RELOAD_WINDOW_MS) return; // just retried — let it surface
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  event.preventDefault(); // suppress the unhandled rejection; we're handling it
  window.location.reload();
});

const root = document.getElementById('root');

if (!root) {
  throw new Error('[Solomon] Root element #root not found. Check your index.html.');
}

// Public inbound consent funnel — rendered standalone (no auth/app providers)
// for sellers. Matches either the /offer path OR an `offer.` subdomain
// (e.g. offer.solomonhq.com), so a clean seller-facing domain serves the page
// at its root. Subdomain match is client-side because the landing page is a
// client route — a CDN rewrite alone wouldn't change window.location.
const isOfferLanding =
  window.location.pathname.startsWith('/offer') ||
  window.location.hostname.split('.')[0] === 'offer';

// Public Privacy Policy page — standalone, works on both hosts (/privacy).
const isPrivacyPage = window.location.pathname.startsWith('/privacy');

createRoot(root).render(
  <StrictMode>
    {isPrivacyPage ? (
      <PrivacyPolicy />
    ) : isOfferLanding ? (
      <OfferLanding />
    ) : (
      <AuthProvider>
        <SolomonProvider>
          <App />
        </SolomonProvider>
      </AuthProvider>
    )}
  </StrictMode>
);
